/**
 * FinancialAccounts - Página de Contas Correntes do módulo Financeiro
 * 
 * Layout premium padronizado com MetricCards, filtros inline e tabela
 * CRUD completo com listagem, criação, edição e soft delete
 * Seletor de banco com busca integrada e filtro por tipo de conta
 * Seção de integração com gateway de pagamento (Asaas)
 * 
 * @author Sistema
 * @version 2.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação da página
 * RELEASE: 2026-03-14 v1.1.0 - Seletor de banco com busca, reordenação de campos
 * RELEASE: 2026-03-14 v1.2.0 - Integração Asaas (API key, teste, badge de status)
 * RELEASE: 2026-03-19 v2.0.0 - Layout premium padronizado (tabela, métricas, filtros)
 */

import { useState, useMemo, useCallback } from 'react';
import { 
  Plus, 
  Search,
  Edit, 
  Trash2, 
  Landmark, 
  Building2, 
  Wifi,
  MoreHorizontal,
  CheckCircle,
  Star,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useFinanceiroContaCorrente, ContaCorrenteFormData, FinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { BankSelector } from '@/components/financial/BankSelector';
import { ProviderIntegrationSection } from '@/components/financial/ProviderIntegrationSection';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { usePermissions } from '@/hooks/usePermissions';

const logger = createLogger('FinancialAccounts');

// ============================================================
// CONSTANTES E TIPOS
// ============================================================

/** Estado inicial do formulário */
const INITIAL_FORM: ContaCorrenteFormData = {
  nome: '',
  banco_codigo: '',
  banco_nome: '',
  agencia: '',
  conta: '',
  tipo_conta: 'corrente',
  titular: '',
  documento_titular: '',
  saldo_inicial: 0,
  is_default: false,
  // Contingência v1.0.0 (2026-06-21): default = checkout transparente do sistema.
  checkout_mode: 'transparent',
  // v2026-06-27: política de cartão em parcelamentos no link público (por provider).
  card_installment_payment_mode: 'pay_all_remaining',
};

/** Labels para tipo de conta */
const TIPO_CONTA_LABELS: Record<string, string> = {
  corrente: 'Corrente',
  poupanca: 'Poupança',
  pagamento: 'Pagamento',
};

// ============================================================
// COMPONENTES AUXILIARES - Padrão Premium CRM
// ============================================================

/** Estilos semânticos para MetricCard - padrão horizontal compacto */
const METRIC_CARD_STYLES = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Props do MetricCard */
interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  variant: keyof typeof METRIC_CARD_STYLES;
}

/**
 * Card de métrica horizontal compacto — padrão unificado
 */
function MetricCard({ title, value, icon: Icon, variant }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Props do EmptyState */
interface EmptyStateProps {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: { label: string; onClick: () => void };
}

/**
 * Estado vazio premium com borda pontilhada e gradiente
 */
function EmptyState({ title, description, icon: Icon, action }: EmptyStateProps) {
  return (
    <div className="relative border-dashed border-2 border-border/50 rounded-xl bg-gradient-to-br from-muted/30 via-background to-muted/10 p-12 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">{description}</p>
      {action && (
        <Button size="sm" onClick={action.onClick}>
          <Plus className="h-4 w-4 mr-2" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export default function FinancialAccounts() {
  const { contas, isLoading, createConta, updateConta, deleteConta } = useFinanceiroContaCorrente();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'financial_accounts';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedConta, setSelectedConta] = useState<FinanceiroContaCorrente | null>(null);
  const [formData, setFormData] = useState<ContaCorrenteFormData>({ ...INITIAL_FORM });
  const [webhookConfigured, setWebhookConfigured] = useState(false);

  // Filtros
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState<string>('all');

  // ============================================================
  // MÉTRICAS - calculadas via useMemo
  // ============================================================
  const stats = useMemo(() => {
    const activeCount = contas.filter(c => c.is_active).length;
    const withProvider = contas.filter(c => !!c.has_provider).length;
    const defaultCount = contas.filter(c => c.is_default).length;
    return {
      total: contas.length,
      active: activeCount,
      withProvider,
      default: defaultCount,
    };
  }, [contas]);

  // ============================================================
  // DADOS FILTRADOS
  // ============================================================
  const filteredContas = useMemo(() => {
    return contas.filter(c => {
      // Filtro por busca (nome ou banco)
      if (search) {
        const term = search.toLowerCase();
        const matchNome = c.nome.toLowerCase().includes(term);
        const matchBanco = c.banco_nome?.toLowerCase().includes(term);
        if (!matchNome && !matchBanco) return false;
      }
      // Filtro por tipo de conta
      if (filterTipo !== 'all' && c.tipo_conta !== filterTipo) return false;
      return true;
    });
  }, [contas, search, filterTipo]);

  // ============================================================
  // HANDLERS - lógica original preservada
  // ============================================================

  /** Busca status do webhook ao abrir edição de conta com provider */
  const fetchWebhookStatus = useCallback(async (conta: FinanceiroContaCorrente) => {
    if (!conta.has_provider || !conta.provider_slug) {
      setWebhookConfigured(false);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('financial-provider', {
        body: {
          action: 'get_status',
          provider_slug: conta.provider_slug,
          conta_corrente_id: conta.id,
        },
      });
      if (error) throw error;
      setWebhookConfigured(!!data?.webhook_configured);
    } catch (err) {
      logger.error('Erro ao buscar status do webhook', { error: err });
      setWebhookConfigured(false);
    }
  }, []);

  /** Abrir modal de criação */
  const handleNew = () => {
    setSelectedConta(null);
    setFormData({ ...INITIAL_FORM });
    setWebhookConfigured(false);
    setFormOpen(true);
  };

  /** Abrir modal de edição */
  const handleEdit = (conta: FinanceiroContaCorrente) => {
    setSelectedConta(conta);
    setFormData({
      nome: conta.nome,
      banco_codigo: conta.banco_codigo || '',
      banco_nome: conta.banco_nome || '',
      agencia: '', // Dados mascarados na listagem — edição requer digitação
      conta: '',   // Dados mascarados na listagem — edição requer digitação
      tipo_conta: conta.tipo_conta || 'corrente',
      titular: conta.titular || '',
      documento_titular: '', // Não exposto pela RPC — edição requer digitação
      saldo_inicial: 0,      // Não exposto pela RPC
      is_default: conta.is_default,
      checkout_mode: conta.checkout_mode || 'transparent',
      card_installment_payment_mode: conta.card_installment_payment_mode || 'pay_all_remaining',
    });
    setFormOpen(true);
    fetchWebhookStatus(conta);
  };

  /** Handler para mudança do tipo de conta */
  const handleTipoContaChange = (value: string) => {
    setFormData({
      ...formData,
      tipo_conta: value,
      banco_codigo: '',
      banco_nome: '',
    });
  };

  /** Handler para seleção de banco no BankSelector */
  const handleBancoChange = (codigo: string, nome: string) => {
    setFormData({ ...formData, banco_codigo: codigo, banco_nome: nome });
  };

  /** Salvar (criar ou editar) */
  const handleSave = () => {
    if (selectedConta) {
      updateConta.mutate({ id: selectedConta.id, ...formData });
    } else {
      createConta.mutate(formData);
    }
    setFormOpen(false);
  };

  /**
   * Handler de exclusão com validação de vínculos
   * Verifica cobranças vinculadas antes de permitir exclusão
   */
  const handleDeleteConta = async (conta: FinanceiroContaCorrente) => {
    // Verificar cobranças vinculadas à conta corrente
    const { count } = await supabase
      .from('financeiro_cobrancas')
      .select('*', { count: 'exact', head: true })
      .eq('conta_corrente_id', conta.id)
      .is('deleted_at', null);

    if (count && count > 0) {
      // Possui vínculos — informar e bloquear exclusão
      await confirmDialog({
        variant: 'info',
        title: 'Não é possível remover',
        description: `A conta "${conta.nome}" possui ${count} cobrança(s) vinculada(s). Desative-a em vez de excluir.`,
        confirmText: 'Entendi',
      });
    } else {
      // Sem vínculos — confirmar exclusão
      const confirmed = await confirmDialog({
        variant: 'danger',
        title: 'Remover conta corrente?',
        description: `Tem certeza que deseja excluir "${conta.nome}"? Esta ação não pode ser desfeita.`,
        confirmText: 'Remover',
      });
      if (confirmed) {
        deleteConta.mutate(conta.id);
      }
    }
  };

  // Verificar se há filtros ativos
  const hasActiveFilters = !!search || filterTipo !== 'all';

  return (
    <div className="flex flex-col h-full">
      {/* ============================================================ */}
      {/* HEADER COMPACTO - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Contas Correntes</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie suas contas bancárias e integrações
          </p>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs" 
            onClick={handleNew}
            className=""
          >
            <Plus className="mr-1.5" />
            Nova Conta
          </Button>
        )}
      </div>

      {/* ============================================================ */}
      {/* KPI CARDS - Padrão premium */}
      {/* ============================================================ */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
          <MetricCard title="Total de Contas" value={stats.total} icon={Landmark} variant="primary" />
          <MetricCard title="Ativas" value={stats.active} icon={CheckCircle} variant="success" />
          <MetricCard title="Com Integração" value={stats.withProvider} icon={Wifi} variant="info" />
          <MetricCard title="Padrão" value={stats.default} icon={Star} variant="purple" />
        </div>
      )}

      {/* ============================================================ */}
      {/* BARRA DE FILTROS - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Filtro por tipo de conta */}
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[150px] h-8 text-xs">
            <SelectValue placeholder="Tipo de conta" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="corrente">Corrente</SelectItem>
            <SelectItem value="poupanca">Poupança</SelectItem>
            <SelectItem value="pagamento">Pagamento</SelectItem>
          </SelectContent>
        </Select>

        {/* Busca */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, banco..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse ml-auto">
            Atualizando...
          </span>
        )}
      </div>

      {/* ============================================================ */}
      {/* CONTEÚDO - TABELA DE CONTAS CORRENTES */}
      {/* ============================================================ */}
      <div className="flex-1 overflow-auto">
        <Card className="border-border/50 overflow-hidden bg-gradient-to-br from-background to-muted/10">
          {/* Header da tabela */}
          <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-foreground">Lista de Contas</h2>
              <Badge variant="secondary" className="font-normal">
                {filteredContas.length} encontrada{filteredContas.length !== 1 ? 's' : ''}
              </Badge>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>

          {/* Conteúdo */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredContas.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={hasActiveFilters ? 'Nenhuma conta encontrada' : 'Nenhuma conta cadastrada'}
                description={hasActiveFilters
                  ? 'Tente buscar por outro termo ou limpe os filtros.'
                  : 'Cadastre sua primeira conta bancária para começar.'
                }
                icon={Landmark}
                action={!hasActiveFilters && hasCreatePermission ? { label: 'Cadastrar Conta', onClick: handleNew } : undefined}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/40">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Banco</TableHead>
                    <TableHead className="font-semibold">Agência / Conta</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Integração</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContas.map((conta) => (
                    <TableRow 
                      key={conta.id}
                      className="group hover:bg-muted/20 transition-colors"
                    >
                      {/* Nome + Titular */}
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{conta.nome}</p>
                            {conta.is_default && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Padrão</Badge>
                            )}
                          </div>
                          {conta.titular && (
                            <p className="text-sm text-muted-foreground">{conta.titular}</p>
                          )}
                        </div>
                      </TableCell>

                      {/* Banco */}
                      <TableCell>
                        {conta.banco_nome ? (
                          <span className="text-sm">
                            {conta.banco_codigo ? `${conta.banco_codigo} - ` : ''}{conta.banco_nome}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Agência / Conta (mascarados pela RPC) */}
                      <TableCell>
                        {conta.agencia_masked || conta.conta_masked ? (
                          <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                            {conta.agencia_masked ? `Ag ${conta.agencia_masked}` : ''}
                            {conta.agencia_masked && conta.conta_masked ? ' / ' : ''}
                            {conta.conta_masked ? `Cc ${conta.conta_masked}` : ''}
                          </code>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Tipo */}
                      <TableCell>
                        <Badge variant="outline">
                          {TIPO_CONTA_LABELS[conta.tipo_conta] || conta.tipo_conta}
                        </Badge>
                      </TableCell>

                      {/* Integração (usa has_provider booleano — sem expor credential_id) */}
                      <TableCell>
                        {conta.has_provider ? (
                          <Badge variant="secondary" className="text-xs">
                            <Wifi className="h-3 w-3 mr-1" />
                            {conta.provider_slug || 'Integrado'}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Status — padrão unificado (Badge outline + StatusDot) */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={conta.is_active ? 'emerald' : 'muted'} />
                          {conta.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </TableCell>

                      {/* Ações */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasEditPermission && (
                              <DropdownMenuItem onClick={() => handleEdit(conta)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {hasDeletePermission && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteConta(conta)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* ============================================================ */}
      {/* MODAL DE CRIAÇÃO/EDIÇÃO - Lógica original preservada */}
      {/* ============================================================ */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedConta ? 'Editar Conta Corrente' : 'Nova Conta Corrente'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* 1. Nome da Conta */}
            <div>
              <Label htmlFor="nome">Nome da Conta *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={e => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Conta Principal"
              />
            </div>

            {/* 2. Tipo de Conta */}
            <div>
              <Label htmlFor="tipo_conta">Tipo de Conta</Label>
              <Select value={formData.tipo_conta} onValueChange={handleTipoContaChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="corrente">Corrente</SelectItem>
                  <SelectItem value="poupanca">Poupança</SelectItem>
                  <SelectItem value="pagamento">Pagamento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 3. Seletor de Banco com busca */}
            <div>
              <Label>Banco</Label>
              <BankSelector
                tipoConta={formData.tipo_conta || ''}
                value={formData.banco_codigo || ''}
                onChange={handleBancoChange}
              />
            </div>

            {/* 4. Agência e Conta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="agencia">Agência</Label>
                <Input
                  id="agencia"
                  value={formData.agencia}
                  onChange={e => setFormData({ ...formData, agencia: e.target.value })}
                  placeholder="0001"
                />
              </div>
              <div>
                <Label htmlFor="conta">Conta</Label>
                <Input
                  id="conta"
                  value={formData.conta}
                  onChange={e => setFormData({ ...formData, conta: e.target.value })}
                  placeholder="12345-6"
                />
              </div>
            </div>

            {/* 5. Titular */}
            <div>
              <Label htmlFor="titular">Titular</Label>
              <Input
                id="titular"
                value={formData.titular}
                onChange={e => setFormData({ ...formData, titular: e.target.value })}
                placeholder="Nome do titular"
              />
            </div>

            {/* 6. CPF/CNPJ do Titular */}
            <div>
              <Label htmlFor="documento_titular">CPF/CNPJ do Titular</Label>
              <Input
                id="documento_titular"
                value={formData.documento_titular}
                onChange={e => setFormData({ ...formData, documento_titular: e.target.value })}
                placeholder="000.000.000-00"
              />
            </div>

            {/* 7. Seção de Integração com Gateway de Pagamento */}
            <ProviderIntegrationSection
              contaCorrenteId={selectedConta?.id || null}
              bancoCodigo={formData.banco_codigo || ''}
              tipoConta={formData.tipo_conta || ''}
              isConnected={!!selectedConta?.has_provider}
              currentProviderSlug={selectedConta?.provider_slug}
              currentEnvironment={selectedConta?.provider_environment}
              providerAccountId={null}
              webhookConfigured={webhookConfigured}
              onWebhookRegistered={() => setWebhookConfigured(true)}
            />

            {/*
             * 8. Modo de checkout exibido ao pagador (contingência v1.0.0 - 2026-06-21)
             *
             * O sistema sempre gera os DOIS links:
             *   • transparente — sessão do checkout próprio (/c/:slug)
             *   • provider     — invoiceUrl/bankSlipUrl nativo do gateway
             *
             * Este seletor define qual será exibido por padrão. Caso o
             * preferido falhe ou ainda não tenha sido gerado, o frontend
             * (resolveCheckoutUrl) faz fallback automático para o outro.
             */}
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
              <Label htmlFor="checkout_mode" className="text-sm font-medium">
                Checkout exibido ao pagador
              </Label>
              <Select
                value={formData.checkout_mode || 'transparent'}
                onValueChange={(value: 'transparent' | 'provider') =>
                  setFormData({ ...formData, checkout_mode: value })
                }
              >
                <SelectTrigger id="checkout_mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transparent">
                    Checkout transparente do sistema (recomendado)
                  </SelectItem>
                  <SelectItem value="provider">
                    Link nativo do provider (contingência)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                O sistema sempre gera os dois links em paralelo. Esta opção define
                qual será apresentado por padrão. Se o preferido não estiver
                disponível, o outro é usado automaticamente como contingência.
              </p>
            </div>

            {/*
             * Política de pagamento de parcelamentos por cartão no link público
             * (v2026-06-27). Visível apenas para contas integradas ao Asaas.
             *
             * Asaas atualmente NÃO suporta pagar somente UMA parcela de um
             * parcelamento via /payWithCreditCard — o gateway sempre confirma
             * todas as parcelas em aberto na mesma transação. Por isso a
             * opção "Somente a parcela acessada" permanece DESABILITADA até
             * que o provider implemente o recurso. Outros providers futuros
             * poderão suportá-la sem mudança nesta UI.
             */}
            {/*
             * Detecção dupla:
             *   - Edição: selectedConta.provider_slug === 'asaas'
             *     (conta já provisionada, fonte de verdade é o backend).
             *   - Criação: ainda não existe provider_slug — usamos o código
             *     do banco selecionado no BankSelector (461 = Asaas).
             * Quando outros providers suportarem a feature, basta estender
             * esta condição (ou mapear código→provider em util compartilhado).
             */}
            {(selectedConta?.provider_slug === 'asaas' ||
              formData.banco_codigo === '461') && (
              <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                <Label
                  htmlFor="card_installment_payment_mode"
                  className="text-sm font-medium"
                >
                  Recebimento de parcelamentos por cartão (link público)
                </Label>
                <Select
                  value={formData.card_installment_payment_mode || 'pay_all_remaining'}
                  onValueChange={(value: 'pay_all_remaining' | 'pay_single_only') =>
                    setFormData({ ...formData, card_installment_payment_mode: value })
                  }
                >
                  <SelectTrigger id="card_installment_payment_mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pay_all_remaining">
                      Quitar todas as parcelas em aberto
                    </SelectItem>
                    <SelectItem value="pay_single_only" disabled>
                      Somente a parcela acessada (indisponível para Asaas)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Quando o cliente paga uma parcela via cartão no link público,
                  o Asaas confirma todas as parcelas em aberto em uma única
                  transação. A opção "Somente a parcela acessada" não é
                  suportada pelo gateway no momento.
                </p>
              </div>
            )}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleSave} 
              disabled={!formData.nome || createConta.isPending || updateConta.isPending}
            >
              {createConta.isPending || updateConta.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ConfirmDialog premium (renderizado pelo hook useConfirmDialog) */}
      {ConfirmDialogComponent}
    </div>
  );
}
